import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'

export interface SimpleVendor {
    id: number
    name: string
    email?: string
}

export interface VendorW9 {
    id: number
    apartment: number
    apartment_name: string
    vendor: SimpleVendor | null
    file: string
    name: string
    ai_summary: string | null
    uploaded_by: number
    created_date: string
    modified_date: string
}

export interface OrgVendorW9Params {
    organization?: number | null
    vendor?: number
}

const QUERY_KEY = ['orgVendorW9s']

export const useOrgVendorW9s = (params?: OrgVendorW9Params) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Build query string for list endpoints (includes all filters)
    const buildQueryString = () => {
        if (!params) return ''
        const searchParams = new URLSearchParams()
        if (params.organization)
            searchParams.append('organization', params.organization.toString())
        if (params.vendor)
            searchParams.append('vendor', params.vendor.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    // Get all vendor W-9s for organization
    const {
        data: vendorW9s,
        isLoading: isLoadingVendorW9s,
        error: errorVendorW9s,
        refetch: refetchVendorW9s,
    } = useQuery<AxiosResponse<VendorW9[]>, Error>({
        queryKey: [...QUERY_KEY, params],
        queryFn: () =>
            axiosInstance.get(`/company/vendor_w9/${buildQueryString()}`),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    // Delete a vendor W-9
    const deleteVendorW9 = useMutation({
        mutationFn: async (id: number) => {
            return axiosInstance.delete(
                `/company/vendor_w9/${id}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Vendor W-9 deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete vendor W-9'
            toast.error(errorMessage)
        },
    })

    const processVendorW9 = useMutation({
        mutationFn: async (id: number) => {
            setProcessingIds((prev) => new Set(prev).add(id))
            return axiosInstance.post(
                `/company/vendor_w9/${id}/process_w9/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, id) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Vendor W-9 analyzed successfully')
        },
        onError: (error: any, id) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            toast.error(error?.response?.data?.error || 'Failed to analyze W-9')
        },
    })

    return {
        vendorW9s: vendorW9s?.data || [],
        isLoadingVendorW9s,
        errorVendorW9s,
        refetchVendorW9s,
        deleteVendorW9,
        processVendorW9,
        processingIds,
        isDeleting: deleteVendorW9.isLoading,
    }
}
